<section class="market-admin-dashboard" aria-label="Manager View">
  <div class="welcome">
    <!-- Cycles Dashboard View -->
    <!-- Breadcrumbs -->
    <div class="breadcrumbs">
      <button type="button" class="breadcrumb-link" (click)="onBackToDashboard()">
        <mat-icon>arrow_back</mat-icon>
        <span>Dashboard</span>
      </button>
      <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
      <span class="breadcrumb-current">{{ managerName }}</span>
    </div>

    <h1>Manager View</h1>
    <div class="user-name" [title]="managerName">{{ managerName }}</div>
    <p>View-only access to this manager's Active Cycles, Past Cycles, and Sub-Managers</p>

    @if (!isLoading && error) {
      <div class="error-message">
        <mat-icon>error</mat-icon>
        {{ error }}
        <button mat-icon-button color="warn" [appBefairTooltip]="'Retry loading data'" (click)="refreshData()">
          <mat-icon>refresh</mat-icon>
        </button>
      </div>
    }

    <div class="cycles-container">
      <!-- Active Cycles Card -->
      <div class="card cycles-card">
        <div class="section-header">
          <h3 class="section-title">
            <mat-icon>schedule</mat-icon>
            Active Cycles
          </h3>
          <span class="section-count">{{ activeCycles.length }}</span>
        </div>
        <div class="cycles-list">
          @if (isLoading) {
            <div class="loading-state">
              <p>Loading active cycles...</p>
            </div>
          } @else if (activeCycles.length === 0) {
            <div class="empty-state">
              <mat-icon>schedule</mat-icon>
              <p>No active cycles found</p>
            </div>
          } @else {
            @for (cycle of activeCycles; track cycle.cycle_id) {
              <div class="cycle-item active-cycle" tabindex="0" (click)="onViewCycle(cycle.cycle_id)" (keyup.enter)="onViewCycle(cycle.cycle_id)">
                <div class="cycle-header">
                  <span class="status-badge status-active">
                    <mat-icon>radio_button_checked</mat-icon>
                    ACTIVE
                  </span>
                </div>
                <div class="cycle-body">
                  <h4 class="cycle-title" [title]="cycle.cycle_name">{{ cycle.cycle_name }}</h4>
                  <div class="cycle-meta">
                    <div class="meta-item">
                      <mat-icon>event</mat-icon>
                      <span>Due: {{ cycle.due_date | date:'dd-MMM-yyyy' }}</span>
                    </div>
                  </div>
                  <div class="progress-section">
                    <div class="progress-info">
                      <span class="progress-text">Progress: {{ cycle.completed_assignments }}/{{ cycle.total_forms }} Forms</span>
                      <span class="progress-percent">{{ cycle.progress }}%</span>
                    </div>
                    <div class="progress-bar">
                      <div class="progress-fill" [style.width.%]="cycle.progress"></div>
                    </div>
                  </div>
                  <button type="button" class="action-link" (click)="onViewCycle(cycle.cycle_id); $event.stopPropagation()">
                    <mat-icon>visibility</mat-icon>
                    View Cycle
                  </button>
                </div>
              </div>
            }
          }
        </div>
      </div>

      <!-- Past Cycles Card -->
      <div class="card cycles-card">
        <div class="section-header">
          <h3 class="section-title">
            <mat-icon>history</mat-icon>
            Past Cycles
          </h3>
          <span class="section-count">{{ totalPastCycles }}</span>
        </div>
        <div class="cycles-list">
          @if (isLoading || isLoadingPastCycles) {
            <div class="loading-state">
              <p>Loading past cycles...</p>
            </div>
          } @else if (pastCycles.length === 0) {
            <div class="empty-state">
              <mat-icon>history</mat-icon>
              <p>No past cycles found</p>
            </div>
          } @else {
            @for (cycle of pastCycles; track cycle.cycle_id) {
              <div class="cycle-item past-cycle" tabindex="0" (click)="onViewCycle(cycle.cycle_id)" (keyup.enter)="onViewCycle(cycle.cycle_id)">
                <div class="cycle-header">
                  <span class="status-badge status-completed">
                    <mat-icon>check_circle</mat-icon>
                    COMPLETED
                  </span>
                </div>
                <div class="cycle-body">
                  <h4 class="cycle-title" [title]="cycle.cycle_name">{{ cycle.cycle_name }}</h4>
                  <div class="cycle-meta">
                    <div class="meta-item">
                      <mat-icon>event_available</mat-icon>
                      <span>Completed: {{ cycle.completed_date | date:'dd-MMM-yyyy' }}</span>
                    </div>
                  </div>
                  <button type="button" class="action-link" (click)="onViewCycle(cycle.cycle_id); $event.stopPropagation()">
                    <mat-icon>visibility</mat-icon>
                    View Cycle
                  </button>
                </div>
              </div>
            }
          }
        </div>

        <!-- Pagination -->
        @if (!isLoadingPastCycles && pastCycles.length > 0) {
          <div class="pagination-wrapper">
            <mat-paginator
              aria-label="Past cycles pagination"
              [length]="totalPastCycles"
              [pageSize]="pageSize"
              [pageIndex]="pageIndex"
              [pageSizeOptions]="pageSizeOptions"
              [showFirstLastButtons]="true"
              (page)="onPageChange($event)" />
          </div>
        }
      </div>
    </div>
  </div>

  <aside class="side" aria-label="Sub Managers" [class.collapsed]="isSidebarCollapsed">
    <div>
      <div class="section-header">
        <button mat-icon-button
                class="sidebar-toggle-btn"
                [attr.aria-label]="isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
                [appBefairTooltip]="isSidebarCollapsed ? 'Expand sub-managers list' : 'Collapse sub-managers list'"
                (click)="toggleSidebar()">
          <mat-icon>{{ isSidebarCollapsed ? 'chevron_left' : 'chevron_right' }}</mat-icon>
        </button>
        @if (!isSidebarCollapsed) {
        <div class="section-title">
          <mat-icon class="section-icon">people</mat-icon>
          Sub Managers
        </div>
        <span class="section-count">{{ subManagers.length }}</span>
        }
      </div>

      <div class="managers-list" [class.collapsed-view]="isSidebarCollapsed">
        @if (isLoadingSubManagers && !isSidebarCollapsed) {
          <div class="loading-state">
            <mat-spinner diameter="40" />
            <p>Loading sub managers...</p>
          </div>
        } @else if (subManagers.length === 0 && !isSidebarCollapsed) {
          <div class="empty-state">
            <mat-icon>people_outline</mat-icon>
            <p>No sub managers found</p>
          </div>
        } @else {
          @for (manager of subManagers; track manager.id) {
            <div class="manager-card"
                 role="button"
                 tabindex="0"
                 [class.selected]="manager.prid === managerPrid"
                 [class.collapsed-card]="isSidebarCollapsed"
                 [attr.title]="manager.first_name + ' ' + manager.last_name"
                 (click)="onViewSubManager(manager)"
                 (keyup.enter)="onViewSubManager(manager)">
              <div class="manager-avatar">
                @if (manager.profileUrl) {
                <img class="manager-avatar-img" width="40" height="40" appDefaultImage [userName]="manager.first_name + ' ' + manager.last_name" [ngSrc]="manager.profileUrl" [alt]="manager.first_name + ' ' + manager.last_name">
                } @else {
                  <div class="manager-avatar-initials">
                    {{ manager.first_name + ' ' + manager.last_name | appUserInitials }}
                  </div>
                }
              </div>
              @if (!isSidebarCollapsed) {
              <div class="manager-info">
                <p class="title manager-name">{{ manager.first_name }} {{ manager.last_name }}</p>
                <p class="sub manager-role">{{ manager.email }}</p>
              </div>
              <div class="manager-action">
                <mat-icon>chevron_right</mat-icon>
              </div>
              }
            </div>
          }
        }
      </div>
    </div>
  </aside>
</section>
